import './styles.scss';

import { ComboBox } from 'src/components/ui/combobox';
import { CreditAppService } from 'src/services/credit-app.service';
import { CreditStatusTableSortType } from 'src/views/pages/credit/status/index';
import { isFirstRender } from 'src/utils/isFirstMount';
import TooltipIcon from '../../../ui/tooltip';
import { useLocalization } from 'src/utils/useLocalization';
import { useTranslation } from 'react-i18next';
import {
    compareDates,
    compareNumbers,
    compareStrings,
    isSiteCanadian,
} from 'src/utils/helpers';
import {
    CreditSearchTypes,
    CreditStatusTableData,
} from 'src/services/models/credit.service';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { matchOnlyNumber, matchSSN } from 'src/utils/validators';
interface ICreditSearchBarProps {
    updateTableContent: Dispatch<SetStateAction<CreditStatusTableData[]>>;
    dealerID: string;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setFailedSearchTerm: Dispatch<SetStateAction<string>>;
    sortType: number;
    sortOrder: boolean;
}

const search = {
    [CreditSearchTypes.SSN]: matchSSN,
    [CreditSearchTypes.APPLICATION_NUMBER]: matchOnlyNumber,
};

export const parseSearch = (searchText: string): CreditSearchTypes => {
    if (searchText === '') {
        return CreditSearchTypes.RECEIVED_IN_MONTHS;
    }

    for (const [searchType, validator] of Object.entries(search)) {
        if (validator(searchText)) {
            return searchType as CreditSearchTypes;
        }
    }
    return CreditSearchTypes.APPLICANT_NAME;
};

export const Search: React.FC<ICreditSearchBarProps> = ({
    updateTableContent,
    dealerID,
    setLoading,
    setFailedSearchTerm,
    sortOrder,
    sortType,
}) => {
    const {
        creditSearch,
        creditStatusFilterOptionToResponseMap,
        creditStatusFilterOptionMap,
        fundingStatus,
    } = useLocalization();
    const { t, i18n } = useTranslation();

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [dateReceived, setDateReceived] = useState<string>('');
    const [resetState, setResetState] = useState<boolean>(false);
    const [records, setRecords] = useState<any[]>([]);

    const firstRender = isFirstRender();

    const service = new CreditAppService();

    const statusFilterOptions: string[] = [
        creditSearch.TABLE_OPTION_STATUS_ALL,
        creditSearch.APPROVED,
        creditSearch.PENDING,
        creditSearch.DECLINED,
        creditSearch.CONDITIONED,
        creditSearch.WITHDRAWN,
    ];

    enum FilterNames {
        Search = 'Search',
        Status = 'Status',
        Date = 'Date',
    }

    const DEFAULT_SEARCH_TIME = '3';

    const resetFilters = async (filtersToReset: FilterNames[]) => {
        const all = !filtersToReset.length;
        setResetState(true);
        (!!all || !!filtersToReset.includes(FilterNames.Search)) &&
            setSearchTerm('');
        (!!all || !!filtersToReset.includes(FilterNames.Status)) &&
            setStatusFilter('');
        (!!all || !!filtersToReset.includes(FilterNames.Date)) &&
            setDateReceived('');
    };

    const handleReset = async () => {
        await resetFilters([]);
        setResetState(false);
        searchDefaultData();
    };

    const searchData = async (searchType?: CreditSearchTypes) => {
        setLoading(true);
        let searchValue = searchType ? dateReceived : DEFAULT_SEARCH_TIME;
        let [errorMsg, i18nErrorMsg] = [
            `${searchValue} ${creditSearch.TABLE_OPTION_MONTHS}`,
            `${searchValue} ${t('CreditSearch.Table.Option.Months')}`,
        ];
        if (searchType == CreditSearchTypes.RECEIVED_IN_MONTHS) {
            await resetFilters([FilterNames.Status, FilterNames.Search]);
            searchValue = dateReceived;
            setResetState(false);
        } else if (
            searchType == CreditSearchTypes.SSN ||
            searchType == CreditSearchTypes.APPLICATION_NUMBER ||
            searchType == CreditSearchTypes.APPLICANT_NAME
        ) {
            await resetFilters([FilterNames.Status, FilterNames.Date]);
            setResetState(false);
            searchValue = searchTerm;
            [errorMsg, i18nErrorMsg] = [searchTerm, searchTerm];
        } else if (searchType == CreditSearchTypes.STATUS) {
            await resetFilters([FilterNames.Date, FilterNames.Search]);
            setResetState(false);
            [errorMsg, i18nErrorMsg] = [
                statusFilter,
                t(creditStatusFilterOptionMap[statusFilter]),
            ];
            searchValue = creditStatusFilterOptionToResponseMap[statusFilter];
        }
        try {
            const response = await service.creditStatusSearch(
                dealerID,
                searchType ?? CreditSearchTypes.RECEIVED_IN_MONTHS,
                searchValue,
            );
            if (response?.data) {
                setRecords(service.mapCreditStatusResponse(response.data));
                setLoading(false);
            }
        } catch {
            setRecords([]);
            i18n.on('languageChanged', function () {
                setFailedSearchTerm(i18nErrorMsg);
            });
            setFailedSearchTerm(errorMsg);
            setLoading(false);
        }
    };

    const searchDefaultData = async () => {
        setResetState(true);
        setDateReceived(DEFAULT_SEARCH_TIME);
        setResetState(false);
        searchData();
    };

    const handleStatusFilterChange = async () => {
        if (statusFilter === creditSearch.TABLE_OPTION_STATUS_ALL) {
            await searchDefaultData();
        } else {
            await searchData(CreditSearchTypes.STATUS);
        }
    };

    const handleDateReceivedChange = async () =>
        searchData(CreditSearchTypes.RECEIVED_IN_MONTHS);

    const creditSearchRequest = async () => searchData(parseSearch(searchTerm));

    useEffect(() => {
        searchDefaultData();
    }, []);

    const shouldMakeRequest: boolean = !firstRender && !resetState;
    const tooltip = isSiteCanadian()
        ? creditSearch.SEARCH_BAR_TOOLTIP_CA
        : creditSearch.SEARCH_BAR_TOOLTIP;

    const filterData = () => {
        const filteredData = [...records];
        return applySort(filteredData);
    };

    useEffect(() => {
        if (shouldMakeRequest) {
            handleDateReceivedChange();
        }
    }, [dateReceived]);

    useEffect(() => {
        if (shouldMakeRequest) {
            handleStatusFilterChange();
        }
    }, [statusFilter]);

    useEffect(() => {
        updateTableContent(filterData());
    }, [sortOrder, sortType, records]);

    const handleEnter = (evt: any) => {
        if (evt.key === 'Enter') {
            creditSearchRequest();
        }
    };

    function applySort(data: any[]) {
        let sorted = [...data];
        switch (sortType) {
            case CreditStatusTableSortType.APPLICATION_NUMBER:
                sorted = compareNumbers(sorted, 'applicationNumber', sortOrder);
                break;
            case CreditStatusTableSortType.SUBMITTED_DATE:
                sorted = compareDates(sorted, 'submittedTime', !sortOrder);
                break;
            case CreditStatusTableSortType.SUBMITTED_BY:
                sorted = compareStrings(sorted, 'submittedBy', sortOrder);
                break;
            case CreditStatusTableSortType.STATUS:
                sorted = compareStrings(sorted, 'status', sortOrder);
                break;
            case CreditStatusTableSortType.COLLATERAL:
                sorted = compareStrings(sorted, 'collateral', sortOrder);
                break;
            case CreditStatusTableSortType.APPLICANT_NAME:
                sorted = compareStrings(sorted, 'name', sortOrder);
                break;
            default:
                sorted = compareDates(sorted, 'submittedTime', false);
                break;
        }
        return sorted;
    }

    return (
        <div className='search-row c-input'>
            <div className='search-div'>
                <div className='c-input search-text'>
                    <span id='searchIcon'>
                        <i
                            className='c-icon c-icon--[semantic-search]'
                            aria-hidden='true'
                            role={'img'}
                        ></i>
                    </span>
                    <input
                        id='searchInput'
                        className='c-input__input'
                        type={'text'}
                        value={searchTerm}
                        placeholder={creditSearch.SEARCH_BAR_PLACEHOLDER}
                        onChange={(evt) =>
                            setSearchTerm(evt.target.value.trim())
                        }
                        onKeyDown={handleEnter}
                        role='searchQuery'
                    />
                    <span
                        id='clearSearch'
                        className={`c-input__addon ${
                            !searchTerm ? 'invisible' : ''
                        }`}
                        onClick={() => handleReset()}
                        style={{ borderColor: '#d1dcde' }}
                        data-testid='clearSearch'
                        role='clearSearch'
                    >
                        <i
                            className='c-icon  c-icon--[semantic-close]'
                            aria-hidden='true'
                            role={'img'}
                        ></i>
                    </span>
                </div>
                <TooltipIcon text={tooltip}></TooltipIcon>
            </div>
            <div className='select-date-range'>
                <ComboBox
                    name={'date_range'}
                    options={['3', '6', '12', '18', '24']}
                    defaultValue={''}
                    placeholder={creditSearch.TABLE_OPTION_DATE_PLACEHOLDER}
                    selectedOption={dateReceived}
                    onSelect={setDateReceived}
                    optionModifier={creditSearch.TABLE_OPTION_MONTHS}
                    prefix={creditSearch.TABLE_OPTION_DATE_PREFIX}
                    shouldDisplayOptionName={true}
                />
            </div>
            <div className='select-status'>
                <ComboBox
                    name={'status'}
                    options={statusFilterOptions}
                    defaultValue={''}
                    placeholder={creditSearch.TABLE_OPTION_STATUS_ALL}
                    onSelect={setStatusFilter}
                    selectedOption={statusFilter}
                />
            </div>
            <div className='reset-search'>
                <button
                    className={'c-btn--secondary c-btn c-btn--small'}
                    type={'button'}
                    onClick={handleReset}
                >
                    <span className='c-btn__text'>
                        {fundingStatus.TABLE_FUNCTION_RESET_FILTER}
                    </span>
                </button>
            </div>
        </div>
    );
};

export default Search;
